import { useEffect, useState, ComponentType } from 'react';
import { getMonth, addMonths, parseISO } from 'date-fns';
import { useSearchParams } from 'react-router-dom';
import Container from '@material-ui/core/Container';
import Skeleton from '@material-ui/core/Skeleton';
import TextField from '@material-ui/core/TextField';
import FormControl from '@material-ui/core/FormControl';
import FormLabel from '@material-ui/core/FormLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import LoadingButton from '@material-ui/lab/LoadingButton';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import { Formik, FieldArray, Form, FastField, Field, ErrorMessage, useFormik } from 'formik';
import {
  getSubscriptionMetadata,
  getSubscriptionProducts,
  postSubscriptionPreference
} from '../../../services/subscriptions';

type SubscriptionMetadata = Awaited<ReturnType<typeof getSubscriptionMetadata>>;

const months = [
  'Enero',
  'Febrero',
  'Marzo',
  'Abril',
  'Mayo',
  'Junio',
  'Julio',
  'Agosto',
  'Septiembre',
  'Octubre',
  'Noviembre',
  'Diciembre'
];

const getMonthIndexFromDate = (dateString: string) => {
  const date = new Date(dateString);
  return getMonth(date);
};

const getNextDeliveryMonthName = (dates: string[], defaultName = 'No definido') => {
  if (!dates || !dates.length) {
    return defaultName;
  }

  const nextDate = dates[0];
  const monthIndex = getMonthIndexFromDate(nextDate);
  return months[monthIndex] ?? defaultName;
};

const getAvailableMonthsLength = (dates?: string[]) => (dates ? dates.length : 0);

const Layout: ComponentType = ({ children }) => (
  <Container maxWidth="md" sx={{ my: 10 }}>
    {children}
  </Container>
);

const LoadingState = () => (
  <Layout>
    <Grid container gap={5}>
      <Grid item xs={12} sx={{ textAlign: 'center' }}>
        <Skeleton height={48} width="500px" sx={{ margin: 'auto' }} />
      </Grid>
      <Grid item xs={12}>
        <Skeleton height={30} width="100%" variant="text" />
      </Grid>
      <Grid item xs={12}>
        <Skeleton height={30} width="200px" sx={{ mb: 4 }} />
      </Grid>
    </Grid>

    <Grid container gap={3}>
      <Grid item xs={12}>
        <Skeleton height={56} width="100%" variant="rectangular" />
      </Grid>
      <Grid item xs={12}>
        <Skeleton height={56} width="100%" variant="rectangular" />
      </Grid>
      <Grid item xs={12}>
        <Skeleton height={125} width="100%" variant="rectangular" />
      </Grid>
      <Grid item xs={12}>
        <Skeleton height={48} width="100%" />
      </Grid>
    </Grid>
  </Layout>
);

function InvalidTokenError() {
  return (
    <Container>
      <Box sx={{ maxWidth: 480, margin: 'auto', textAlign: 'center', mt: 6, mb: 6 }}>
        <Box>
          <Typography variant="h3" paragraph>
            Lo sentimos, ya no puedes ver esta página!
          </Typography>
        </Box>
        <Typography sx={{ color: 'text.secondary' }}>
          Puedes comunicarte con nosotros para poder ayudarte.
        </Typography>
      </Box>
    </Container>
  );
}

const useProductTypeProducts = (productTypeId?: string) => {
  const [options, setOptions] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const subToken = useSubscriptionToken();

  useEffect(() => {
    if (productTypeId && subToken) {
      setLoading(true);
      setError(false);
      getSubscriptionProducts({
        token: subToken,
        productTypeId
      })
        .then(({ items }) => {
          const options = items.map(({ id, name }) => ({ id, label: name }));
          setOptions(options);
        })
        .catch((error) => {
          console.error('error', error);
          setError(true);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [productTypeId, subToken]);

  return {
    loading,
    error,
    options
  };
};

const useSubscriptionToken = () => {
  const [queryParams] = useSearchParams();
  const subToken = queryParams.get('sub_token');
  return subToken;
};

const useSubscriptionMetadata = () => {
  const [metadata, setMetadata] = useState<SubscriptionMetadata>();
  const [loading, setLoading] = useState(true);
  const [validToken, setValidToken] = useState(true);
  const subToken = useSubscriptionToken();

  useEffect(() => {
    if (!subToken) {
      setValidToken(false);
      return;
    }

    setLoading(true);
    getSubscriptionMetadata({ token: subToken })
      .then((subscriptionMetadata) => {
        setMetadata(subscriptionMetadata);
      })
      .catch(() => {
        setValidToken(false);
      })
      .finally(() => setLoading(false));
  }, [subToken, setMetadata]);

  return {
    validToken,
    loading,
    metadata
  };
};

function generateDates(startDate: string, remainingMonths: number) {
  const dates: string[] = [];
  const currentDate = parseISO(startDate);
  for (let month = 1; month < remainingMonths; month++) {
    const nextDate = addMonths(currentDate, month).toISOString();
    dates.push(nextDate);
  }

  return dates;
}

const SubscriptionProductPicker = () => {
  const { metadata, validToken, loading } = useSubscriptionMetadata();
  const [productTypeId, setProductTypeId] = useState<string>();
  const [queryParams] = useSearchParams();
  const productTypeIdParam = queryParams.get('productTypeId');
  const productIdParam = queryParams.get('productId');
  const { options: products, loading: loadingProducts } = useProductTypeProducts(productTypeId);
  const subToken = useSubscriptionToken();

  const formikAPI = useFormik({
    initialValues: {
      productTypeId: productTypeIdParam,
      productId: productIdParam,
      notes: ''
    },
    onSubmit: async (values, api) => {
      const { productTypeId, productId, notes } = values;
      if (productTypeId && productId && subToken) {
        api.setSubmitting(true);
        postSubscriptionPreference({
          token: subToken,
          productTypeId: Number(productTypeId),
          productId: Number(productId),
          notes
        })
          .then(console.log.bind(null, 'response from post'))
          .catch(console.error.bind(null, 'error from post'))
          .finally(() => {
            api.setSubmitting(false);
          });
      }
    }
  });

  if (loading) {
    return <LoadingState />;
  }

  if (!validToken || !metadata) {
    return <InvalidTokenError />;
  }

  const dates = generateDates(metadata.startAt, metadata.remainingMonths);
  const binTypes = metadata?.types ?? [];
  const fullname = [metadata?.first_name, metadata?.last_name].filter(Boolean).join(' ');
  const availableMonths = metadata?.remainingMonths;

  return (
    <Layout>
      <Grid container gap={4}>
        <Grid item xs={12}>
          <Typography variant="h3" textAlign="center" sx={{ mb: 4 }}>
            Detalles de tu próximo pedido
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Typography variant="h6" sx={{ mb: 4 }} textAlign="justify">
            Hola {fullname}, por favor selecciona el detalle de tu próximo pedido para el mes de{' '}
            {getNextDeliveryMonthName(dates)}
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Typography variant="h6" sx={{ mb: 4 }} textAlign="justify">
            Tienes {availableMonths} meses disponibles
          </Typography>
        </Grid>
      </Grid>

      <form onSubmit={formikAPI.handleSubmit}>
        <Grid container gap={3} direction="column">
          <Grid item xs={12}>
            <FormControl fullWidth>
              <FormLabel htmlFor="bin-type">Tipo de grano</FormLabel>
              <Select
                id="bin-type"
                name="productTypeId"
                value={formikAPI.values.productTypeId}
                onChange={(event) => {
                  const productTypeId = String(event.target.value);
                  setProductTypeId(productTypeId);
                  formikAPI.handleChange(event);
                }}
              >
                {binTypes.map((bin) => (
                  <MenuItem key={bin.id} value={bin.id}>
                    {bin.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth>
              <FormLabel htmlFor="coffe-type">Tipo de café</FormLabel>
              <Select
                id="coffe-type"
                disabled={loadingProducts || !products.length}
                value={formikAPI.values.productId}
                name="productId"
                onChange={formikAPI.handleChange}
              >
                {products.map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth>
              <FormLabel htmlFor="notes">Comentarios</FormLabel>
              <TextField
                multiline
                fullWidth
                rows={4}
                maxRows={6}
                inputProps={{ maxLength: 255 }}
                id="notes"
                name="notes"
                value={formikAPI.values.notes}
                onChange={formikAPI.handleChange}
              />
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <LoadingButton
              fullWidth
              variant="contained"
              size="large"
              type="submit"
              loading={formikAPI.isSubmitting}
            >
              Enviar pedido
            </LoadingButton>
          </Grid>
        </Grid>
      </form>
    </Layout>
  );
};

export default SubscriptionProductPicker;
